import React, { useState } from "react";
import { View, Text, StyleSheet, Dimensions, Platform } from "react-native";
import LhButton from "../../../components/LhButton";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import FontFamily from "../../../constants/fonts";
import NHOEmployeeSelector from "./NHOEmployeeSelector";
import { useTheme } from "../../../theme";

// Props for NHOWelcome component
interface NHOWelcomeProps {
  onAdminClick: () => void;
  onBeginClick?: () => void;
  onEmployeeProfileClick: (employeeId: string) => void;
  testID?: string;
}

const NHOWelcome: React.FC<NHOWelcomeProps> = ({
  onAdminClick,
  onBeginClick,
  onEmployeeProfileClick,
  testID,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  // State for employee selector modal
  const [isEmployeeModalVisible, setIsEmployeeModalVisible] = useState(false);

  // Function to handle employee selection
  const handleEmployeeSelect = (employeeId: string) => {
    setIsEmployeeModalVisible(false);
    onEmployeeProfileClick(employeeId);
  };
  // Helper function to check if device is iPad specifically
  const isIPadView = () => {
    const windowWidth = Dimensions.get("window").width;
    return Platform.OS === "ios" && windowWidth >= 768;
  };

  // Helper function to check if device is iPhone specifically
  const isIPhoneView = () => {
    const windowWidth = Dimensions.get("window").width;
    return Platform.OS === "ios" && windowWidth < 768;
  };

  // Helper function to check if device is in mobile/phone layout
  const isMobileView = () => {
    const windowWidth = Dimensions.get("window").width;
    return (
      (Platform.OS === "android" && windowWidth < 480) ||
      (Platform.OS === "web" && windowWidth < 768)
    );
  };

  const isIPad = isIPadView();
  const isIPhone = isIPhoneView();
  const isMobile = isMobileView();

  return (
    <View style={styles.welcomeCard} testID={testID || "nho-welcome"}>
      <View
        style={[
          styles.welcomeContainer,
          isIPhone && styles.welcomeContainerIPhone,
          isIPad && styles.welcomeContainerIPad
        ]}
      >
        <View style={styles.welcomeLeftContent}>
          {/* <MaterialIcons name="people" size={24} color="#0066cc" /> */}
          <Text
            style={[
              styles.welcomeText,
              isDarkTheme && { color: colors.text.primary },
            ]}
          >
            New Hire Onboarding
          </Text>
        </View>
        {isIPhone ? (
          // iPhone: Employee Profile on first line, Admin + Begin on second line
          <View style={styles.beginButtonContainerIPhone} testID="nho-welcome-buttons">
            
            <View style={styles.beginButtonRowIPhoneSecond}>
              {onBeginClick && (
                <LhButton
                  text="Begin"
                  variant="primary"
                  size="small"
                  iconTrailing="arrow-forward"
                  iconSize={20}
                  onPress={onBeginClick}
                  isLoading={false}
                  disabled={false}
                  style={styles.beginButton}
                />
              )}
              <LhButton
                text="Admin"
                variant="secondary"
                size="small"
                iconTrailing="settings"
                iconSize={20}
                onPress={onAdminClick}
                isLoading={false}
                disabled={false}
                style={styles.beginButton2}
              />
              
            </View>
            <View style={styles.beginButtonRowIPhoneFirst}>
              <LhButton
                text="Employee Profile"
                variant="secondary"
                size="small"
                iconTrailing="person"
                iconSize={20}
                onPress={() => setIsEmployeeModalVisible(true)}
                isLoading={false}
                disabled={false}
                style={styles.beginButton1}
              />
            </View>
          </View>
        ) : (
          // iPad and Web: all buttons in one row
          <View
            style={isIPad || !isMobile ? styles.beginButtonRow : styles.beginButtonRowMobile}
            testID="nho-welcome-buttons"
          >
            <LhButton
              text="Employee Profile"
              variant="secondary"
              size="small"
              iconTrailing="person"
              iconSize={20}
              onPress={() => setIsEmployeeModalVisible(true)}
              isLoading={false}
              disabled={false}
              style={styles.beginButton1}
            />
            <LhButton
              text="Admin"
              variant="secondary"
              size="small"
              iconTrailing="settings"
              iconSize={20}
              onPress={onAdminClick}
              isLoading={false}
              disabled={false}
              style={styles.beginButton2}
            />
            {onBeginClick && (
              <LhButton
                text="Begin"
                variant="primary"
                size="small"
                iconTrailing="arrow-forward"
                iconSize={20}
                onPress={onBeginClick}
                isLoading={false}
                disabled={false}
                style={styles.beginButton}
              />
            )}
          </View>
        )}
      </View>
      {/* Employee Selection Modal */}
      <NHOEmployeeSelector
        isModalVisible={isEmployeeModalVisible}
        closeModal={() => setIsEmployeeModalVisible(false)}
        onEmployeeSelect={handleEmployeeSelect}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  welcomeCard: {
    
  },
  welcomeContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 0,
    paddingBottom: 16,
    flexWrap: Platform.OS === "web" ? "nowrap" : "wrap",
  },
  welcomeContainerIPhone: {
    flexDirection: "column",
    alignItems: "flex-start",
  },
  welcomeContainerIPad: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "nowrap",
  },
  welcomeLeftContent: {
    flexDirection: "row",
    alignItems: "center",
    flexShrink: 1,
    marginLeft:10
  },
  welcomeText: {
    // Updated to match design: H4 heading style
    color: "#3A3A3A",
    fontFamily: FontFamily.bold,
    fontSize: 
      Platform.OS === "ios" && Dimensions.get("window").width >= 768 
        ? 24 // iPad
        : Platform.OS === "ios" && Dimensions.get("window").width < 768
        ? 22 // iPhone
        : 28, // Web and others
    fontStyle: "normal",
    fontWeight: "700",
    lineHeight: 
      Platform.OS === "ios" && Dimensions.get("window").width >= 768 
        ? 34 // iPad
        : Platform.OS === "ios" && Dimensions.get("window").width < 768
        ? 32 // iPhone
        : 40, // Web and others
    marginLeft: 0,
  },
  beginButtonRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: Platform.OS === "ios" && Dimensions.get("window").width >= 768 ? 8 : 13,
    alignItems: "center",
    width: "auto",
    marginTop: 0,
  },
  beginButtonRowMobile: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    width: "auto",
    marginTop: 20,
    gap: 17,
  },
  beginButtonContainerIPhone: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gap: 10,
    marginTop: 20,
  },
  beginButtonRowIPhoneFirst: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    width: "100%",
  },
  beginButtonRowIPhoneSecond: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    width: "100%",
    gap: 10,
  },
  beginButtonRowIPhone: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    width: "100%",
    marginTop: 5,
  },
  beginButton: {
    width:
      Dimensions.get("window").width < 400
        ? 90
        : Platform.OS === "ios" && Dimensions.get("window").width >= 768
        ? 100 // iPad: reduced width
        : Platform.OS === "web"
        ? "30%"
        : 120,
    fontSize: Dimensions.get("window").width < 400 ? 12 : 14,
  },
  beginButton1: {
    width:
      Dimensions.get("window").width < 400
        ? 150
        : Platform.OS === "ios" && Dimensions.get("window").width >= 768
        ? 140 // iPad: reduced width for Employee Profile
        : Platform.OS === "web"
        ? "50%"
        : 200,
    fontSize: Dimensions.get("window").width < 400 ? 12 : 14,
  },
  beginButton2: {
    width:
      Dimensions.get("window").width < 400
        ? 90
        : Platform.OS === "ios" && Dimensions.get("window").width >= 768
        ? 100 // iPad: reduced width for Admin
        : Platform.OS === "web"
        ? "40%"
        : 120,
    fontSize: Dimensions.get("window").width < 400 ? 12 : 14,
  },
});

export default NHOWelcome;
